import { useCallback } from 'react';
import { isOmnichannelGreetingAPIEnabled, isOmnichannelSubscriptionErrorpageEnabled } from '@helpcenter/feature-flags';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import type { PayloadError } from 'relay-runtime';
import { generateUniqueClientID } from 'relay-runtime';
import { doc, p } from '@atlaskit/adf-utils/builders';
import { initialAIMessageHCExperience, initialIntentMessageHCExperience } from '../../common/experiences';
import { addMessageToConnection } from '../add-message-to-connection';
import { updateOmniChannelConversationMeta } from '../omnichannel-update-conversation-meta';
import {
    addGreetingMessageToConnection,
    addOptimisticResponseToConnection,
    clearTimer,
    deleteNodeFromConnection,
    resetTimer,
    updateMessageStatusToHelpseeker,
    updateRelayRecordId,
} from '../store-actions';
import type {
    createConversationWithMessageMutation,
    createConversationWithMessageMutation$data,
} from './__generated__/createConversationWithMessageMutation.graphql';
import { createConversationWithMessageExperience } from './experiences';

interface CreateConversationWithMessageArgs {
    workspaceAri: string;
    conversationContextAri: string;
    onCompleted?: (response: createConversationWithMessageMutation$data, errors: PayloadError[] | null) => void;
    onError?: (error: Error) => void;
    isTestConversation?: boolean;
    message: string;
    subscriptionId: string;
    connectionId: string;
}

interface HandleMutationErrorArgs {
    nodeId: string;
    message: string;
    connectionId: string;
    subscriptionId: string;
}

export const useCreateConversationWithMessage = () => {
    const [commitMutation, isLoading] = useMutation<createConversationWithMessageMutation>(graphql`
        mutation createConversationWithMessageMutation(
            $workspaceAri: ID!
            $conversationContextAri: ID!
            $isTestChannel: Boolean = false
            $message: String!
            $subscriptionId: String!
        ) {
            jsmChat {
                initializeAndSendMessage(
                    workspaceAri: $workspaceAri
                    input: {
                        conversationContextAri: $conversationContextAri
                        isTestChannel: $isTestChannel
                        channelExperienceId: HELPCENTER
                        message: $message
                        subscriptionId: $subscriptionId
                    }
                ) @optIn(to: "JsmChatVAConversationAPI") {
                    success
                    errors {
                        message
                        extensions {
                            statusCode
                        }
                    }
                    initializeAndSendMessageResponse {
                        conversationId
                        conversation {
                            node {
                                id
                                content
                                authorType
                                contentType
                            }
                        }
                    }
                }
            }
        }
    `);

    const environment = useRelayEnvironment();

    const handleMutationError = useCallback(
        ({ message, nodeId, connectionId, subscriptionId }: HandleMutationErrorArgs) => {
            updateOmniChannelConversationMeta({ environment, isReplyPending: false });
            if (!isOmnichannelSubscriptionErrorpageEnabled()) {
                deleteNodeFromConnection({ environment, connectionId, nodeId });
            }
            const metadata = {
                connectionId,
                subscriptionId,
            };

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            createConversationWithMessageExperience.failure({
                metadata,
            });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            initialIntentMessageHCExperience.abort({
                metadata,
            });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            initialAIMessageHCExperience.abort({
                metadata,
            });
            addMessageToConnection({
                message,
                environment,
                connectionId,
                authorType: 'CombinedAPIError',
            });
        },
        [environment]
    );

    const createConversationWithMessage = useCallback(
        ({
            workspaceAri,
            conversationContextAri,
            connectionId,
            onError,
            message,
            subscriptionId,
            isTestConversation = false,
        }: CreateConversationWithMessageArgs) => {
            const nodeId = generateUniqueClientID();
            const content = JSON.stringify(doc(p(message)));
            addOptimisticResponseToConnection({
                environment,
                nodeId,
                content,
                connectionId,
                message,
            });
            updateOmniChannelConversationMeta({ environment, isReplyPending: true });
            updateMessageStatusToHelpseeker({ environment });
            resetTimer({ environment });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            createConversationWithMessageExperience.start();
            if (isOmnichannelGreetingAPIEnabled()) {
                addGreetingMessageToConnection({
                    environment,
                    connectionId,
                    nodeId: generateUniqueClientID(),
                });
            }

            return commitMutation({
                onCompleted: (response) => {
                    if (
                        !response?.jsmChat?.initializeAndSendMessage?.success ||
                        !response?.jsmChat?.initializeAndSendMessage?.initializeAndSendMessageResponse?.conversationId
                    ) {
                        handleMutationError({
                            nodeId,
                            message,
                            connectionId,
                            subscriptionId,
                        });
                        return;
                    }
                    const responseData = response.jsmChat?.initializeAndSendMessage?.initializeAndSendMessageResponse;
                    updateOmniChannelConversationMeta({
                        environment,
                        workspaceAri,
                        conversationContextAri,
                        conversationId: responseData?.conversationId,
                    });
                    clearTimer(environment);
                    if (responseData.conversationId && responseData.conversation?.node?.id) {
                        updateRelayRecordId({
                            environment,
                            nodeId,
                            newId: responseData.conversation?.node?.id as string,
                        });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    createConversationWithMessageExperience.success({
                        metadata: {
                            subscriptionId,
                            conversationId: responseData.conversation?.node?.id,
                        },
                    });
                },
                onError: (error) => {
                    handleMutationError({
                        nodeId,
                        message,
                        connectionId,
                        subscriptionId,
                    });
                    onError?.(error);
                },
                variables: {
                    workspaceAri,
                    conversationContextAri,
                    subscriptionId,
                    isTestChannel: isTestConversation,
                    message: content,
                },
            });
        },
        [commitMutation, environment, handleMutationError]
    );

    return {
        isLoading,
        createConversationWithMessage,
    };
};
